/**
 * Upload Lessons Page
 * Form for teachers to upload video lessons
 */

import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Navbar, Footer, Input, Button } from '../../components';
import SEO from '../../components/common/SEO';

const UploadLessons = () => {
  const { courseId } = useParams();
  const [loading, setLoading] = useState(false);
  const [lessons, setLessons] = useState([
    {
      id: 1,
      title: 'Introduction to React',
      duration: '12:30',
      videoFile: null,
      resourceFile: null,
      status: 'uploaded',
    },
    {
      id: 2,
      title: 'Setting Up Development Environment',
      duration: '18:45',
      videoFile: null,
      resourceFile: null,
      status: 'uploaded',
    },
  ]);
  const [newLesson, setNewLesson] = useState({
    title: '',
    duration: '',
    videoFile: null,
    videoPreview: '',
    resourceFile: null,
    resourceName: '',
  });

  const handleNewLessonChange = (e) => {
    const { name, value } = e.target;
    setNewLesson((prev) => ({ ...prev, [name]: value }));
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewLesson((prev) => ({
        ...prev,
        videoFile: file,
        videoPreview: file.name,
      }));
    }
  };

  const handleResourceChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewLesson((prev) => ({
        ...prev,
        resourceFile: file,
        resourceName: file.name,
      }));
    }
  };

  const handleAddLesson = async () => {
    if (!newLesson.title || !newLesson.videoFile) return;
    
    setLoading(true);
    try {
      // Simulated upload
      await new Promise((resolve) => setTimeout(resolve, 1500));
      
      setLessons((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          title: newLesson.title,
          duration: newLesson.duration || '00:00',
          videoFile: newLesson.videoFile,
          resourceFile: newLesson.resourceFile,
          status: 'uploaded',
        },
      ]);
      
      setNewLesson({
        title: '',
        duration: '',
        videoFile: null,
        videoPreview: '',
        resourceFile: null,
        resourceName: '',
      });
    } catch (err) {
      console.error('Error uploading lesson:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLesson = (lessonId) => {
    setLessons((prev) => prev.filter((l) => l.id !== lessonId));
  };

  return (
    <div className="lh-upload-lessons min-h-screen bg-dark-50">
      <SEO title="Upload Lessons" noIndex />
      <Navbar />

      <main className="lh-upload-lessons__main pt-20 pb-12">
        <div className="lh-upload-lessons__container max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="lh-upload-lessons__breadcrumb flex items-center gap-2 text-sm text-dark-500 mb-6">
            <Link to="/teacher/dashboard" className="lh-upload-lessons__breadcrumb-link hover:text-primary-500">Dashboard</Link>
            <span className="lh-upload-lessons__breadcrumb-separator">/</span>
            <Link to="/teacher/courses" className="lh-upload-lessons__breadcrumb-link hover:text-primary-500">Courses</Link>
            <span className="lh-upload-lessons__breadcrumb-separator">/</span>
            <Link to={`/teacher/courses/${courseId}`} className="lh-upload-lessons__breadcrumb-link hover:text-primary-500">Course Details</Link>
            <span className="lh-upload-lessons__breadcrumb-separator">/</span>
            <span className="lh-upload-lessons__breadcrumb-current text-dark-900">Upload Lessons</span>
          </nav>

          {/* Header */}
          <div className="lh-upload-lessons__header mb-8">
            <h1 className="lh-upload-lessons__title text-2xl md:text-3xl font-bold text-dark-900 mb-2">
              Upload Lessons
            </h1>
            <p className="lh-upload-lessons__subtitle text-dark-500">
              Add video lessons and resources to your course
            </p>
          </div>

          {/* Existing Lessons */}
          <div className="lh-upload-lessons__existing card p-6 mb-6">
            <h2 className="lh-upload-lessons__existing-title text-lg font-bold text-dark-900 mb-4">
              Course Lessons ({lessons.length})
            </h2>
            
            {lessons.length > 0 ? (
              <div className="lh-upload-lessons__list space-y-3">
                {lessons.map((lesson, index) => (
                  <div
                    key={lesson.id}
                    className={`lh-upload-lessons__lesson lh-upload-lessons__lesson--${lesson.id} flex items-center gap-4 p-4 bg-dark-50 rounded-lg`}
                  >
                    <span className="lh-upload-lessons__lesson-number w-8 h-8 bg-primary-100 text-primary-500 rounded-full flex items-center justify-center font-semibold text-sm">
                      {index + 1}
                    </span>
                    <div className="lh-upload-lessons__lesson-info flex-1">
                      <h4 className="lh-upload-lessons__lesson-title font-medium text-dark-900">{lesson.title}</h4>
                      <div className="lh-upload-lessons__lesson-meta flex items-center gap-3 text-sm text-dark-500">
                        <span className="lh-upload-lessons__lesson-duration flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {lesson.duration}
                        </span>
                        <span className="lh-upload-lessons__lesson-status text-green-500 flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Uploaded
                        </span>
                      </div>
                    </div>
                    <div className="lh-upload-lessons__lesson-actions flex items-center gap-2">
                      <button className="lh-upload-lessons__lesson-edit p-2 text-dark-400 hover:text-primary-500 transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteLesson(lesson.id)}
                        className="lh-upload-lessons__lesson-delete p-2 text-dark-400 hover:text-red-500 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="lh-upload-lessons__empty text-dark-500 text-center py-8">
                No lessons uploaded yet. Add your first lesson below.
              </p>
            )}
          </div>

          {/* Add New Lesson */}
          <div className="lh-upload-lessons__new card p-6">
            <h2 className="lh-upload-lessons__new-title text-lg font-bold text-dark-900 mb-4">Add New Lesson</h2>
            
            <div className="lh-upload-lessons__form space-y-4">
              <div className="lh-upload-lessons__form-row grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Lesson Title"
                  name="title"
                  placeholder="Enter lesson title"
                  value={newLesson.title}
                  onChange={handleNewLessonChange}
                  required
                  className="lh-upload-lessons__input lh-upload-lessons__input--title"
                />
                <Input
                  label="Duration"
                  name="duration"
                  placeholder="e.g., 15:30"
                  value={newLesson.duration}
                  onChange={handleNewLessonChange}
                  className="lh-upload-lessons__input lh-upload-lessons__input--duration"
                />
              </div>

              {/* Video Upload */}
              <div className="lh-upload-lessons__video">
                <label className="lh-upload-lessons__video-label block text-sm font-medium text-dark-700 mb-1.5">
                  Video File <span className="lh-upload-lessons__required text-red-500">*</span>
                </label>
                {newLesson.videoPreview ? (
                  <div className="lh-upload-lessons__video-preview flex items-center gap-4 p-4 bg-dark-50 rounded-lg">
                    <div className="lh-upload-lessons__video-icon w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-primary-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                    <div className="lh-upload-lessons__video-info flex-1">
                      <p className="lh-upload-lessons__video-name font-medium text-dark-900">{newLesson.videoPreview}</p>
                      <p className="lh-upload-lessons__video-status text-sm text-dark-500">Ready to upload</p>
                    </div>
                    <button
                      onClick={() => setNewLesson((prev) => ({ ...prev, videoFile: null, videoPreview: '' }))}
                      className="lh-upload-lessons__video-remove text-red-500 hover:text-red-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <label className="lh-upload-lessons__video-dropzone flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-dark-200 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors">
                    <svg className="lh-upload-lessons__video-dropzone-icon w-8 h-8 text-dark-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                    </svg>
                    <span className="lh-upload-lessons__video-dropzone-text text-sm text-dark-500">Click to upload video</span>
                    <span className="lh-upload-lessons__video-dropzone-hint text-xs text-dark-400">MP4, WebM up to 500MB</span>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {/* Resource Upload */}
              <div className="lh-upload-lessons__resource">
                <label className="lh-upload-lessons__resource-label block text-sm font-medium text-dark-700 mb-1.5">
                  Resources (Optional)
                </label>
                {newLesson.resourceName ? (
                  <div className="lh-upload-lessons__resource-preview flex items-center gap-4 p-4 bg-dark-50 rounded-lg">
                    <div className="lh-upload-lessons__resource-icon w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-secondary-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z" />
                      </svg>
                    </div>
                    <div className="lh-upload-lessons__resource-info flex-1">
                      <p className="lh-upload-lessons__resource-name font-medium text-dark-900">{newLesson.resourceName}</p>
                      <p className="lh-upload-lessons__resource-status text-sm text-dark-500">Ready to upload</p>
                    </div>
                    <button
                      onClick={() => setNewLesson((prev) => ({ ...prev, resourceFile: null, resourceName: '' }))}
                      className="lh-upload-lessons__resource-remove text-red-500 hover:text-red-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <label className="lh-upload-lessons__resource-dropzone flex items-center gap-4 p-4 border border-dark-200 rounded-lg cursor-pointer hover:border-primary-400 transition-colors">
                    <div className="lh-upload-lessons__resource-dropzone-icon w-10 h-10 bg-dark-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                    <span className="lh-upload-lessons__resource-dropzone-text text-sm text-dark-500">Add resource file (PDF, ZIP)</span>
                    <input
                      type="file"
                      accept=".pdf,.zip"
                      onChange={handleResourceChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              <div className="lh-upload-lessons__submit flex justify-end">
                <Button
                  onClick={handleAddLesson}
                  variant="primary"
                  loading={loading}
                  disabled={!newLesson.title || !newLesson.videoFile}
                  className="lh-upload-lessons__submit-btn"
                >
                  Add Lesson
                </Button>
              </div>
            </div>
          </div>

          {/* Save & Continue */}
          <div className="lh-upload-lessons__actions flex justify-between mt-6">
            <Link
              to={`/teacher/courses/${courseId}`}
              className="lh-upload-lessons__back text-dark-500 hover:text-dark-700 font-medium"
            >
              ← Back to Course
            </Link>
            <Button variant="primary" className="lh-upload-lessons__save">
              Save & Continue
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default UploadLessons;
